import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './leaderboard.css';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { apiUrl } from '../../utils/apiUrl';
import LeaderboardLayout, { dedupeByUserIdBestScore, normalizeLeaderboardMatch } from './LeaderboardLayout';

function parsePositiveRank(v) {
  const n = parseInt(String(v ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function Leaderboard() {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);
  const [matches, setMatches] = useState([]);
  const [currentUserMeta, setCurrentUserMeta] = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [loading, setLoading] = useState(true);
  const [feedbackDone, setFeedbackDone] = useState(() => !!location.state?.feedbackSubmitted);

  useEffect(() => {
    if (!user || !adminToken) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      setFetchError('');
      try {
        const uid = user.userId || user.id;
        const resp = await fetch(apiUrl('/gameMatchUsers'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok || !data.success) {
          setMatches([]);
          setCurrentUserMeta(null);
          setFetchError(
            data.error || data.message || (resp.status ? `Request failed (${resp.status})` : 'Leaderboard request failed')
          );
          return;
        }
        let raw = Array.isArray(data.matches) ? data.matches : [];
        if (raw.length === 0 && data.currentUser) {
          const cu = data.currentUser;
          const id = cu.userId ?? cu.id;
          if (id != null && String(id).trim() !== '') {
            raw = [{ ...cu, userId: id, isSelf: true, matchPercent: 0 }];
          }
        }
        const normalized = raw.map(normalizeLeaderboardMatch);
        setMatches(dedupeByUserIdBestScore(normalized));
        const cu = data.currentUser;
        const apiRank = data.currentUserRank;
        if (cu && (cu.userId != null || cu.id != null)) {
          const cuNorm = normalizeLeaderboardMatch({ ...cu, userId: cu.userId ?? cu.id, isSelf: true });
          setCurrentUserMeta({
            rank: parsePositiveRank(apiRank),
            score: cuNorm.score ?? 0,
            time: cuNorm.time,
            name: cuNorm.name || 'You',
          });
        } else {
          setCurrentUserMeta(
            parsePositiveRank(apiRank)
              ? { rank: parsePositiveRank(apiRank), score: 0, time: '00:00', name: 'You' }
              : null
          );
        }
      } catch (e) {
        console.error('gameMatchUsers', e);
        if (!cancelled) {
          setMatches([]);
          setCurrentUserMeta(null);
          setFetchError(e.message || 'Network error loading leaderboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  useEffect(() => {
    if (!user || !adminToken) return;
    let cancelled = false;
    (async () => {
      try {
        const uid = user.userId || user.id;
        const resp = await fetch(apiUrl('/spotGameStatus'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const raw = data.userFeedback;
        if (data.success && raw != null && String(raw).trim() !== '') {
          setFeedbackDone(true);
        }
      } catch (e) {
        console.error('spotGameStatus (leaderboard)', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  const footer = (
    <div className="lb-feedback-wrap">
      {feedbackDone ? (
        <div className="lb-feedback-done">Thanks — you’ve submitted feedback for this session.</div>
      ) : (
        <Link to="/feedback" replace className="lb-feedback-link">
          Rate your experience
        </Link>
      )}
    </div>
  );

  return (
    <LeaderboardLayout
      title="Leaderboard"
      matches={matches}
      loading={loading}
      fetchError={fetchError}
      user={user}
      currentUserMeta={currentUserMeta}
      themeData={themeData}
      footer={footer}
    />
  );
}

export default Leaderboard;
